import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import { getApiUrl } from './config';

function Customers() {
    const role = localStorage.getItem("role") || "user";
    const isAdmin = role === "admin";
    const [customers, setCustomers] = useState([]);
    const [newCustomer, setNewCustomer] = useState({ name: '', contact: '', address: '', email: '', discount_rate: 0, opening_balance: 0 });
    const [createAccess, setCreateAccess] = useState(false);
    const [accessEmail, setAccessEmail] = useState('');
    const [accessPassword, setAccessPassword] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [accessCustomer, setAccessCustomer] = useState(null);
    const [accessForm, setAccessForm] = useState({ email: '', password: '' });

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async () => {
        try {
            const res = await axios.get(`${getApiUrl()}/customers`);
            setCustomers(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleSaveCustomer = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                if (!isAdmin) return alert("Only admin can update records");
                await axios.put(`${getApiUrl()}/customers/${editingId}`, newCustomer);
            } else {
                if (createAccess) {
                    if (!isAdmin) {
                        alert("Only admin can create login access");
                        return;
                    }
                    const emailToUse = (accessEmail || newCustomer.email || '').trim();
                    if (!emailToUse) {
                        alert("Email is required to create customer login access");
                        return;
                    }
                    if (!accessPassword.trim()) {
                        alert("Password is required to create customer login access");
                        return;
                    }
                }
                await axios.post(`${getApiUrl()}/customers`, {
                    ...newCustomer,
                    create_access: isAdmin && createAccess,
                    access_email: (accessEmail || newCustomer.email || '').trim(),
                    access_password: accessPassword
                });
            }
            setNewCustomer({ name: '', contact: '', address: '', email: '', discount_rate: 0, opening_balance: 0 });
            setCreateAccess(false);
            setAccessEmail('');
            setAccessPassword('');
            setEditingId(null);
            fetchCustomers();
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                (typeof err?.response?.data === "string" ? err.response.data : "") ||
                (editingId ? "Failed to update customer" : "Failed to add customer");
            alert(message);
        }
    };

    const startEdit = (c) => {
        setEditingId(c.id);
        setNewCustomer({
            name: c.name || '',
            contact: c.contact || '',
            address: c.address || '',
            email: c.email || '',
            discount_rate: parseFloat(c.discount_rate) || 0,
            opening_balance: parseFloat(c.opening_balance) || 0
        });
        setCreateAccess(false);
        setAccessEmail('');
        setAccessPassword('');
    };

    const cancelEdit = () => {
        setEditingId(null);
        setNewCustomer({ name: '', contact: '', address: '', email: '', discount_rate: 0, opening_balance: 0 });
        setCreateAccess(false);
        setAccessEmail('');
        setAccessPassword('');
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this customer?")) return;
        try {
            await axios.delete(`${getApiUrl()}/customers/${id}`);
            fetchCustomers();
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                "Failed to delete customer";
            alert(message);
        }
    };

    const openAccess = (c) => {
        setAccessCustomer(c);
        setAccessForm({ email: (c.access_email || c.email || '').trim(), password: '' });
    };

    const closeAccess = () => {
        setAccessCustomer(null);
        setAccessForm({ email: '', password: '' });
    };

    const saveAccess = async () => {
        if (!accessCustomer) return;
        if (!isAdmin) return alert("Only admin can update records");
        if (!accessForm.email.trim()) return alert("Email is required");
        try {
            await axios.post(`${getApiUrl()}/customers/${accessCustomer.id}/access`, {
                email: accessForm.email.trim(),
                password: accessForm.password
            });
            alert("Access saved");
            closeAccess();
            fetchCustomers();
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                "Failed to save access";
            alert(message);
        }
    };

    const deleteAccess = async () => {
        if (!accessCustomer) return;
        if (!isAdmin) return alert("Only admin can update records");
        if (!window.confirm("Delete login access for this customer?")) return;
        try {
            await axios.delete(`${getApiUrl()}/customers/${accessCustomer.id}/access`);
            alert("Access deleted");
            closeAccess();
            fetchCustomers();
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                "Failed to delete access";
            alert(message);
        }
    };

    return (
        <Layout>
            <div className="container mt-4">
                <h2>Customers</h2>
                <form onSubmit={handleSaveCustomer} className="mb-4">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <input type="text" className="form-control" placeholder="Customer Name" value={newCustomer.name} onChange={e => setNewCustomer({...newCustomer, name: e.target.value})} onFocus={(e) => e.target.select()} required />
                        </div>
                        <div className="col-md-3">
                            <input type="text" className="form-control" placeholder="Contact" value={newCustomer.contact} onChange={e => setNewCustomer({...newCustomer, contact: e.target.value})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-md-3">
                            <input type="text" className="form-control" placeholder="Address" value={newCustomer.address} onChange={e => setNewCustomer({...newCustomer, address: e.target.value})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-md-3">
                            <input type="email" className="form-control" placeholder="Email" value={newCustomer.email} onChange={e => setNewCustomer({...newCustomer, email: e.target.value})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-md-3">
                            <input
                                type="number"
                                className="form-control"
                                placeholder="Discount Rs/Kg"
                                value={newCustomer.discount_rate}
                                onChange={e => setNewCustomer({ ...newCustomer, discount_rate: parseFloat(e.target.value) })}
                                onFocus={(e) => e.target.select()}
                            />
                        </div>
                        {isAdmin && (
                            <div className="col-md-3">
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Opening Balance"
                                    value={newCustomer.opening_balance}
                                    onChange={e => setNewCustomer({ ...newCustomer, opening_balance: parseFloat(e.target.value) || 0 })}
                                    onFocus={(e) => e.target.select()}
                                />
                            </div>
                        )}
                        <div className="col-12">
                            {!editingId && isAdmin && (
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={createAccess}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setCreateAccess(checked);
                                            if (checked) setAccessEmail((newCustomer.email || '').trim());
                                        }}
                                        id="createCustomerAccess"
                                    />
                                    <label className="form-check-label" htmlFor="createCustomerAccess">
                                        Create Login Access for this Customer
                                    </label>
                                </div>
                            )}
                        </div>

                        {!editingId && isAdmin && createAccess && (
                            <>
                                <div className="col-md-4">
                                    <input
                                        type="email"
                                        className="form-control"
                                        placeholder="Login Email"
                                        value={accessEmail}
                                        onChange={e => setAccessEmail(e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        required
                                    />
                                </div>
                                <div className="col-md-4">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Login Password"
                                        value={accessPassword}
                                        onChange={e => setAccessPassword(e.target.value)}
                                        onFocus={(e) => e.target.select()}
                                        required
                                    />
                                </div>
                            </>
                        )}

                        <div className="col-md-2 d-grid">
                            <button type="submit" className={`btn ${editingId ? 'btn-warning' : 'btn-primary'}`}>{editingId ? 'Update' : 'Add'}</button>
                        </div>
                        {editingId && (
                            <div className="col-md-2 d-grid">
                                <button type="button" className="btn btn-secondary" onClick={cancelEdit}>Cancel</button>
                            </div>
                        )}
                    </div>
                </form>

                {accessCustomer && (
                    <div className="card p-3 mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Customer Login Access</h5>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={closeAccess}>Close</button>
                        </div>
                        <div className="text-muted mt-2">{accessCustomer.name}</div>
                        <div className="row g-3 mt-1">
                            <div className="col-md-6">
                                <label className="form-label">Login Email</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    value={accessForm.email}
                                    onChange={e => setAccessForm({ ...accessForm, email: e.target.value })}
                                    onFocus={(e) => e.target.select()}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">New Password (optional)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={accessForm.password}
                                    onChange={e => setAccessForm({ ...accessForm, password: e.target.value })}
                                    onFocus={(e) => e.target.select()}
                                />
                            </div>
                            <div className="col-12">
                                <button type="button" className="btn btn-primary me-2" onClick={saveAccess}>Save Access</button>
                                {(accessCustomer.access_email || accessCustomer.access_user_id) && (
                                    <button type="button" className="btn btn-danger" onClick={deleteAccess}>Delete Access</button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="table-responsive">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Contact</th>
                                <th>Address</th>
                                <th>Email</th>
                                <th>Disc Rs/Kg</th>
                                <th>Login</th>
                                {isAdmin && <th>Action</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {customers.map(c => (
                                <tr key={c.id}>
                                    <td>{c.name}</td>
                                    <td>{c.contact}</td>
                                    <td>{c.address}</td>
                                    <td>{c.email}</td>
                                    <td>{parseFloat(c.discount_rate || 0).toFixed(2)}</td>
                                    <td>{c.access_email || '-'}</td>
                                    {isAdmin && (
                                        <td>
                                            <button type="button" className="btn btn-warning btn-sm me-1" onClick={() => startEdit(c)}>Edit</button>
                                            <button type="button" className="btn btn-info btn-sm me-1" onClick={() => openAccess(c)}>Access</button>
                                            <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id)}>Delete</button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
}

export default Customers;
